import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { AiReplySuggestion } from 'shared/api/ai-reply-suggestion';

interface Options {
  /**
   * Laat de AI nu een voorstel schrijven. Duurt een paar seconden.
   *
   * `instruction` is wat de medewerker in het instructieveld typte, of `undefined`
   * wanneer hij niets meegaf.
   */
  generate: (instruction?: string) => Promise<AiReplySuggestion>;
  /**
   * Haalt een al klaarstaand voorstel op bij het openen (helpdesk: dat wordt bij een
   * inkomende huurdersmail automatisch gemaakt). Weglaten waar dat niet bestaat.
   */
  loadPending?: () => Promise<AiReplySuggestion | null>;
  /**
   * Meldt de backend dat een bewaard voorstel afgehandeld is, zodat het niet
   * opnieuw opduikt. Weglaten waar voorstellen niet bewaard worden.
   */
  close?: (suggestion: AiReplySuggestion, outcome: 'used' | 'dismissed') => Promise<void>;
  /**
   * Wat er standaard in het instructieveld staat (de huisregels voor een antwoord).
   * Het veld begint dus niet leeg; aanpassen of wissen mag altijd.
   */
  defaultInstruction?: string;
  /** Uit zolang het venster gesloten is of de sessie nog niet klaar is. */
  enabled?: boolean;
  /** Voor Sentry, bv. `{ module: 'helpdesk', ticketId: 42 }`. */
  context?: Record<string, unknown>;
}

/**
 * Houdt het AI-antwoordvoorstel van één antwoordvenster bij.
 *
 * De aanroeper geeft mee *hoe* er een voorstel opgehaald of geschreven wordt (de
 * endpoints verschillen per module); deze hook doet de toestand eromheen: laden,
 * fouten, overnemen, wegklikken en opruimen bij het sluiten.
 *
 * **Er vertrekt nooit iets vanzelf.** Overnemen zet de tekst in het venster — niets
 * meer. Verzenden blijft een aparte handeling.
 */
export function useAiReplySuggestion({
  generate,
  loadPending,
  close,
  defaultInstruction = '',
  enabled = true,
  context,
}: Options) {
  const [suggestion, setSuggestion] = useState<AiReplySuggestion | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Wat de medewerker de AI meegeeft. Begint op de standaardinstructie en blijft
   * staan na het schrijven, zodat je hem kan bijstellen en "Opnieuw schrijven" kan
   * duwen — dat is de manier waarop je naar een bruikbaar voorstel toewerkt.
   */
  const [instruction, setInstruction] = useState(defaultInstruction);

  // Zodat een antwoord dat binnenkomt nadat het venster sloot, niets meer zet.
  const actief = useRef(true);
  useEffect(() => {
    actief.current = true;
    return () => {
      actief.current = false;
    };
  }, []);

  // Het klaarstaande voorstel ophalen bij het openen. Geen melding als het
  // mislukt: zonder voorstel werkt het antwoordvenster gewoon, en de knop staat er.
  useEffect(() => {
    if (!enabled || !loadPending) return;

    let gestopt = false;
    (async () => {
      try {
        const klaarstaand = await loadPending();
        if (!gestopt && klaarstaand?.body) setSuggestion(klaarstaand);
      } catch (err) {
        console.error('[AI-voorstel] Klaarstaand voorstel ophalen mislukt:', err);
      }
    })();

    return () => {
      gestopt = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Alles opruimen zodra het venster sluit: bij de volgende opening hoort het
  // voorstel bij de mail van dán, en staat de standaardinstructie er weer.
  useEffect(() => {
    if (!enabled) {
      setSuggestion(null);
      setError(null);
      setIsGenerating(false);
      setInstruction(defaultInstruction);
    }
  }, [enabled, defaultInstruction]);

  const requestSuggestion = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const nieuw = await generate(instruction.trim() || undefined);
      if (!actief.current) return;
      if (!nieuw?.body) {
        setError('De AI gaf geen tekst terug. Probeer het straks opnieuw.');
        return;
      }
      setSuggestion(nieuw);
    } catch (err) {
      if (!actief.current) return;
      // De uitleg komt van de backend; die is bruikbaar ("OpenAI is onbereikbaar")
      // en hoort dus in het blok te staan, niet enkel in de console.
      setError(
        (err instanceof Error && err.message) ||
          'Het AI-voorstel kon niet geschreven worden',
      );
      Sentry.captureException(err, {
        tags: { feature: 'ai-reply-suggestion' },
        extra: context,
      });
    } finally {
      if (actief.current) setIsGenerating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generate, instruction, context]);

  /** Sluit het voorstel af in de UI, en bij de backend als die het bewaart. */
  const afsluiten = useCallback(
    (outcome: 'used' | 'dismissed') => {
      const open = suggestion;
      setSuggestion(null);
      setError(null);
      if (!open || !close) return;
      close(open, outcome).catch((err) => {
        // Niet fataal: in het slechtste geval staat het voorstel er de volgende
        // keer nog. Dat is minder erg dan het antwoord ophouden.
        console.error('[AI-voorstel] Voorstel afsluiten mislukt:', err);
      });
    },
    [suggestion, close],
  );

  return {
    suggestion,
    isGenerating,
    error,
    /** Wat de medewerker de AI meegeeft; leeg = geen instructie. */
    instruction,
    setInstruction,
    /** Zet het instructieveld terug op de standaardinstructie. */
    resetInstruction: () => setInstruction(defaultInstruction),
    /** Of er iets aan de standaardinstructie gewijzigd is (dan pas is herstellen zinvol). */
    isInstructionModified: instruction !== defaultInstruction,
    /** Vraag een (nieuw) voorstel, met de huidige instructie erbij. */
    requestSuggestion,
    /** Het voorstel is overgenomen in het bericht. */
    markUsed: () => afsluiten('used'),
    /** Het voorstel is weggeklikt. */
    dismiss: () => afsluiten('dismissed'),
  };
}
